/**
 * Audio utility functions for speech-to-text and audio processing
 */

export interface AudioTranscriptionResult {
  transcript: string
  confidence?: number
  error?: string
}

export interface AudioRecordingOptions {
  maxDuration?: number
  sampleRate?: number
  echoCancellation?: boolean
  noiseSuppression?: boolean
}

/**
 * Convert audio blob to base64 string for API transmission
 */
export const audioBlobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = reader.result as string
      // Remove data URL prefix to get just the base64 data
      const base64 = result.split(',')[1]
      resolve(base64)
    }
    reader.onerror = () => reject(new Error('Failed to convert audio to base64'))
    reader.readAsDataURL(blob)
  })
}

/**
 * Convert base64 string back to audio blob
 */
export const base64ToAudioBlob = (
  base64: string,
  mimeType: string = 'audio/webm;codecs=opus'
): Blob => {
  const byteCharacters = atob(base64)
  const byteNumbers = new Array(byteCharacters.length)

  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }

  const byteArray = new Uint8Array(byteNumbers)
  return new Blob([byteArray], { type: mimeType })
}

/**
 * Transcribe audio using Web Speech API
 */
export const transcribeAudioWithWebAPI = (audioBlob: Blob): Promise<AudioTranscriptionResult> => {
  return new Promise((resolve) => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition

    if (!SpeechRecognition) {
      resolve({
        transcript: '',
        error: 'Speech recognition not supported in this browser',
      })
      return
    }

    const recognition = new SpeechRecognition()
    recognition.continuous = true
    recognition.interimResults = false
    recognition.lang = 'en-US'

    let finalTranscript = ''
    let confidence = 0

    recognition.onresult = (event) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript + ' '
          confidence = Math.max(confidence, event.results[i][0].confidence || 0)
        }
      }
    }

    recognition.onend = () => {
      resolve({
        transcript: finalTranscript.trim(),
        confidence,
      })
    }

    recognition.onerror = (event) => {
      resolve({
        transcript: '',
        error: `Speech recognition error: ${event.error}`,
      })
    }

    // Create audio URL and play it for recognition
    const audioUrl = URL.createObjectURL(audioBlob)
    const audio = new Audio(audioUrl)

    audio.onended = () => {
      URL.revokeObjectURL(audioUrl)
    }

    audio.play()
    recognition.start()
  })
}

/**
 * Check if audio recording is supported
 */
export const isAudioRecordingSupported = (): boolean => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
}

/**
 * Check if speech synthesis is supported
 */
export const isSpeechSynthesisSupported = (): boolean => {
  return 'speechSynthesis' in window
}

/**
 * Check if speech recognition is supported
 */
export const isSpeechRecognitionSupported = (): boolean => {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition)
}

/**
 * Get available audio input devices
 */
export const getAudioInputDevices = async (): Promise<MediaDeviceInfo[]> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices()
    return devices.filter((device) => device.kind === 'audioinput')
  } catch (error) {
    console.error('Error getting audio input devices:', error)
    return []
  }
}

/**
 * Format duration in seconds to MM:SS format
 */
export const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Estimate spoken duration for a text string (in seconds)
 */
export const estimateSpeechDuration = (text: string, wordsPerMinute = 130): number => {
  if (!text) {
    return 5
  }

  const words = text.trim().split(/\s+/).length
  const minutes = words / Math.max(wordsPerMinute, 80)
  const seconds = Math.max(5, Math.ceil(minutes * 60))
  return seconds
}

/**
 * Extract text content from message for TTS
 */
export const extractTextForTTS = (content: any): string => {
  if (typeof content === 'string') {
    // Remove markdown formatting for cleaner speech
    return content
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/`(.*?)`/g, '$1') // Remove code
      .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
      .replace(/#{1,6}\s/g, '') // Remove headers
      .replace(/>\s/g, '') // Remove blockquotes
      .replace(/\n+/g, ' ') // Replace line breaks with spaces
      .trim()
  }

  if (content && typeof content === 'object') {
    // For form content, extract the main message
    if (content.message) {
      return extractTextForTTS(content.message)
    }

    // For other structured content, try to find text fields
    const textFields = ['text', 'title', 'description', 'content']
    for (const field of textFields) {
      if (content[field]) {
        return extractTextForTTS(content[field])
      }
    }
  }

  return ''
}

// Extend Window interface for TypeScript
declare global {
  interface Window {
    SpeechRecognition: any
    webkitSpeechRecognition: any
  }
}
